/**
 * Brand types for the Ballee/Flow split.
 *
 * - ballee: Public community + agency surface — ballee.co (apex)
 * - flow: B2B SaaS for performing-arts agencies — flow.ballee.co
 *
 * The brand registry + host rules now live in `@kit/shared/brand` (the single
 * source of truth importable by packages AND apps). This module is the thin
 * apps/web facade that adds the Next.js request-aware helpers (`getBrand`)
 * and the SEO URL builders. See
 * `docs/wip/active/WIP_unifying_brand_resolution_for_outbound_comms_2026_06_29.md`.
 */
import {
  BRAND_REGISTRY,
  resolveBrandFromHost,
  DEFAULT_BRAND as SHARED_DEFAULT_BRAND,
  type BrandKey,
} from '@kit/shared/brand';

export type Brand = BrandKey;

/** Strip the protocol from a registry URL to recover the bare canonical host. */
function domainOf(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/**
 * Brand configuration with display names and canonical domains, DERIVED from
 * the shared registry (no parallel literal). `domain` is the canonical host
 * used by getBrandUrl() to build absolute URLs in sitemap, robots.txt,
 * llms.txt, JSON-LD, and metadata.
 */
export const BRAND_CONFIG = {
  ballee: {
    name: BRAND_REGISTRY.ballee.name,
    description: BRAND_REGISTRY.ballee.description,
    domain: domainOf(BRAND_REGISTRY.ballee.url),
  },
  flow: {
    name: BRAND_REGISTRY.flow.name,
    description: BRAND_REGISTRY.flow.description,
    domain: domainOf(BRAND_REGISTRY.flow.url),
  },
} satisfies Record<
  Brand,
  { name: string; description: string; domain: string }
>;

/**
 * Default brand when hostname can't be determined.
 */
const DEFAULT_BRAND: Brand = SHARED_DEFAULT_BRAND;

/**
 * Detect brand from a hostname string. Delegates to the single host-rule
 * authority in `@kit/shared/brand` so apps + packages + the edge proxy share
 * one rule set.
 */
export function getBrandFromHostname(hostname: string): Brand {
  return resolveBrandFromHost(hostname);
}

/**
 * Get current brand in server components by reading the x-brand header
 * set by middleware.
 *
 * NOTE: This function uses next/headers and can only be called from
 * Server Components or Server Actions. For the import to work with
 * Turbopack, we dynamically import headers() to avoid tainting this
 * file for client bundles.
 */
export async function getBrand(): Promise<Brand> {
  const { headers } = await import('next/headers');
  const headerStore = await headers();
  const brand = headerStore.get('x-brand');

  if (brand === 'ballee' || brand === 'flow') return brand;

  return DEFAULT_BRAND;
}

/**
 * Get the base URL for a specific brand.
 */
export function getBrandUrl(brand: Brand): string {
  const isProduction = process.env.NODE_ENV === 'production';

  if (!isProduction) {
    // In development / preview deploys, both brands run on localhost or a preview URL
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

    if (siteUrl && isValidUrl(siteUrl)) {
      return siteUrl;
    }

    // NEXT_PUBLIC_SITE_URL may be set without protocol on Vercel preview deploys
    if (siteUrl && isValidUrl(`https://${siteUrl}`)) {
      return `https://${siteUrl}`;
    }

    return 'http://localhost:3012';
  }

  return `https://${BRAND_CONFIG[brand].domain}`;
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Build a cross-domain URL to link from one brand to another.
 */
export function buildCrossBrandUrl(targetBrand: Brand, path: string): string {
  const baseUrl = getBrandUrl(targetBrand);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Check if a brand is the "other" brand (useful for cross-domain CTAs).
 */
export function getOtherBrand(brand: Brand): Brand {
  return brand === 'ballee' ? 'flow' : 'ballee';
}

/**
 * Brand-aware SEO context. Bundles the values that schema generators
 * and metadata helpers need so call sites pass one object instead of
 * threading url/name/description individually.
 */
export interface BrandSeoContext {
  brand: Brand;
  name: string;
  url: string;
  description: string;
  logo: string;
}

/**
 * Resolve the SEO context for a brand. Pure (sync) — call sites that
 * need brand-aware schemas should `await getBrand()` first and pass
 * the result here.
 */
export function getBrandSeoContext(brand: Brand): BrandSeoContext {
  const config = BRAND_CONFIG[brand];
  const url = getBrandUrl(brand);
  return {
    brand,
    name: config.name,
    url,
    description: config.description,
    logo: `${url}/images/logo.png`,
  };
}
